import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import { Modal, Button, Spinner, Form, Row, Col, Alert } from "react-bootstrap";
import api, { BASE_URL } from "../../Utils/api";
import {
  FiBell,
  FiCheckCircle,
  FiCopy,
  FiCreditCard,
  FiHardDrive,
  FiImage,
  FiKey,
  FiLayout,
  FiLock,
  FiRefreshCw,
  FiSettings,
  FiShield,
  FiTrash2,
  FiUpload,
  FiXCircle,
} from "react-icons/fi";
import { GlobalContext } from "../../Utils/GlobalContext";
import { Salvar, Cancelar } from "../Generals/buttons";
import SelecaoPlano from "./SelecaoPlano";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import "./EmpresasConfiguracoesModal.css";


const OPCOES_LINHAS_GRID   = [10, 20, 30, 40, 50];
const ACCEPT_IMAGES        = "image/jpeg,image/png,image/gif,image/webp";
const TAMANHOS_PADRAO_MB   = [
  { value: 1,   label: "1 MB"   },
  { value: 2,   label: "2 MB"   },
  { value: 4,   label: "4 MB"   },
  { value: 5,   label: "5 MB"   },
  { value: 10,  label: "10 MB"  },
  { value: 20,  label: "20 MB"  },
  { value: 50,  label: "50 MB"  },
  { value: 100, label: "100 MB" },
];

const ABAS = [
  { key: "geral",         label: "Geral",         icon: FiSettings },
  { key: "plano",         label: "Plano",         icon: FiCreditCard },
  { key: "armazenamento", label: "Armazenamento", icon: FiHardDrive },
  { key: "interface",     label: "Interface",     icon: FiLayout },
  { key: "privacidade",   label: "Privacidade",   icon: FiShield },
  { key: "comunicacao",   label: "Comunicação",   icon: FiBell },
  { key: "api",           label: "API",           icon: FiKey },
];

const EmpresasConfiguracoesModal = ({
  show,
  handleClose,
  empresaId,
  entidadeID,
  empresaNome,
  abaInicial = "geral",
}) => {
  const { globalData, setGlobalData } = useContext(GlobalContext);
  const isAbonada = globalData?.mensalidade_abonada === "S";
  const ABAS_VISIVEIS = isAbonada ? ABAS.filter((a) => a.key !== "plano") : ABAS;
  const { toast } = useFeedback();
  const idEntidade = entidadeID || empresaId;
  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      id_entidade: idEntidade,
    }),
    [idEntidade]
  );

  const [abaAtiva, setAbaAtiva] = useState("geral");

  const [tamanhoMaximoArquivo,     setTamanhoMaximoArquivo]     = useState(4);
  const [lgpdOcultarDados,         setLgpdOcultarDados]         = useState("N");
  const [numeroLinhasGrid,         setNumeroLinhasGrid]         = useState(10);
  const [numeroMaxArquivosPorDoc,  setNumeroMaxArquivosPorDoc]  = useState(0);
  const [permiteArquivosEmAditivo, setPermiteArquivosEmAditivo] = useState("S");
  const [agruparNotificacoes,      setAgruparNotificacoes]      = useState("S");
  const [alertasVencimentoDiario,  setAlertasVencimentoDiario]  = useState("N");
  const [logotipo,                 setLogotipo]                 = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [error,    setError]    = useState(null);
  const fileInputRef = useRef(null);
  const selecaoPlanoRef = useRef(null);

  // ── API Key ──────────────────────────────────────────────────────────────────
  const [apiKeyInfo,     setApiKeyInfo]     = useState(null);   // { prefix, created_at, last_used_at }
  const [apiKeyGerada,   setApiKeyGerada]   = useState(null);   // chave bruta exibida 1x
  const [apiKeyCopiada,  setApiKeyCopiada]  = useState(false);
  const [apiKeyGerando,  setApiKeyGerando]  = useState(false);
  const [apiKeyRevogando, setApiKeyRevogando] = useState(false);

  const fetchApiKey = useCallback(async () => {
    if (!idEntidade) return;
    try {
      const { data } = await api.get(
        `${BASE_URL}/api/apiKeys/${idEntidade}`,
        { headers: authHeaders }
      );
      const ativa = data.chaves?.find((c) => c.situacao === "A") || null;
      setApiKeyInfo(ativa);
    } catch {
      setApiKeyInfo(null);
    }
  }, [idEntidade, authHeaders]);

  const handleGerarApiKey = async () => {
    if (!window.confirm("Gerar uma nova chave revogará a chave atual. Deseja continuar?")) return;
    setApiKeyGerando(true);
    setApiKeyGerada(null);
    setApiKeyCopiada(false);
    try {
      const { data } = await api.post(
        `${BASE_URL}/api/apiKeys/${idEntidade}`,
        {},
        { headers: authHeaders }
      );
      setApiKeyGerada(data.chave);
      setApiKeyInfo({ key_prefix: data.prefix, created_at: new Date().toISOString(), last_used_at: null });
      toast.success("Chave gerada. Copie-a agora — ela não será exibida novamente.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erro ao gerar chave.");
    } finally {
      setApiKeyGerando(false);
    }
  };

  const handleRevogarApiKey = async () => {
    if (!window.confirm("Revogar a chave impedirá todas as integrações que a utilizam. Confirmar?")) return;
    setApiKeyRevogando(true);
    try {
      await api.delete(`${BASE_URL}/api/apiKeys/${idEntidade}`, { headers: authHeaders });
      setApiKeyInfo(null);
      setApiKeyGerada(null);
      toast.success("Chave revogada com sucesso.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erro ao revogar chave.");
    } finally {
      setApiKeyRevogando(false);
    }
  };

  const handleCopiarChave = () => {
    if (!apiKeyGerada) return;
    navigator.clipboard.writeText(apiKeyGerada).then(() => {
      setApiKeyCopiada(true);
      setTimeout(() => setApiKeyCopiada(false), 3000);
    });
  };

  // ── fetch ────────────────────────────────────────────────────────────────────
  const fetchConfiguracoes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(
        `${BASE_URL}/api/empresasConfiguracoes/listarConfiguracoes/${idEntidade}`,
        { params: { incluir_logotipo: "S" }, headers: authHeaders }
      );
      if (data) {
        setTamanhoMaximoArquivo(data.tamanho_maximo_arquivo_mb || 4);
        setLgpdOcultarDados(data.lgpd_ocultar_dados || "N");
        const linhas = Number(data.numero_linhas_grid) || 10;
        setNumeroLinhasGrid(OPCOES_LINHAS_GRID.includes(linhas) ? linhas : 10);
        const rawMax = data.numero_max_arquivos_por_doc;
        setNumeroMaxArquivosPorDoc(Number.isInteger(rawMax) && rawMax >= 0 ? rawMax : 0);
        setPermiteArquivosEmAditivo(data.permite_arquivos_em_aditivo === "N" ? "N" : "S");
        setAgruparNotificacoes(data.agrupar_notificacoes === "N" ? "N" : "S");
        setAlertasVencimentoDiario(data.alertas_vencimento_diario === "S" ? "S" : "N");
        const log = data.logotipo;
        let logoVal = null;
        if (log) {
          if (typeof log === "string") {
            logoVal = log.startsWith("data:") ? log : `data:image/png;base64,${log}`;
          } else if (log.type === "Buffer" && Array.isArray(log.data)) {
            const bytes = new Uint8Array(log.data);
            let binary = "";
            for (let i = 0; i < bytes.length; i += 8192)
              binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
            logoVal = `data:image/png;base64,${btoa(binary)}`;
          }
        }
        setLogotipo(logoVal);
      }
    } catch {
      setTamanhoMaximoArquivo(4); setLgpdOcultarDados("N"); setNumeroLinhasGrid(10);
      setNumeroMaxArquivosPorDoc(0); setPermiteArquivosEmAditivo("S"); setAgruparNotificacoes("S");
      setAlertasVencimentoDiario("N"); setLogotipo(null); setApiKeyInfo(null);
    } finally {
      setLoading(false);
    }
  }, [idEntidade, authHeaders]);

  useEffect(() => {
    if (!show) return;
    setError(null); setAbaAtiva(abaInicial || "geral");
    setApiKeyGerada(null); setApiKeyCopiada(false);
    if (idEntidade) {
      fetchConfiguracoes();
      fetchApiKey();
    } else {
      setTamanhoMaximoArquivo(4); setLgpdOcultarDados("N"); setNumeroLinhasGrid(10);
      setNumeroMaxArquivosPorDoc(0); setPermiteArquivosEmAditivo("S"); setAgruparNotificacoes("S");
      setAlertasVencimentoDiario("N"); setLogotipo(null);
      setApiKeyInfo(null);
      setLoading(false);
    }
  }, [show, idEntidade, fetchConfiguracoes, fetchApiKey, abaInicial]);

  // ── logo ─────────────────────────────────────────────────────────────────────
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > tamanhoMaximoArquivo * 1024 * 1024) {
      setError(`Arquivo excede o limite de ${tamanhoMaximoArquivo} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setLogotipo(reader.result); setError(null); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveLogo = () => {
    setLogotipo(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── salvar ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const maxArquivos = Number(numeroMaxArquivosPorDoc);
    if (!Number.isInteger(maxArquivos) || maxArquivos < 0) {
      setError("Número máximo de arquivos por documento deve ser um inteiro ≥ 0.");
      return;
    }
    if (selecaoPlanoRef.current?.precisaFormaPagamento?.()) {
      setAbaAtiva("plano");
      setError("Selecione a forma de pagamento da assinatura antes de salvar.");
      return;
    }
    if (selecaoPlanoRef.current?.temAssinaturaPendenteBloqueando?.()) {
      setAbaAtiva("plano");
      setError("Há um plano aguardando pagamento. Conclua a fatura antes de escolher outro.");
      return;
    }
    setSalvando(true); setError(null);
    try {
      let mensagemFinal = "Configurações salvas com sucesso!";
      let resultadoPlano = null;

      if (selecaoPlanoRef.current?.temPlanoPendente?.()) {
        resultadoPlano = await selecaoPlanoRef.current.aplicarPlanoPendente();
        if (resultadoPlano === null) {
          return;
        }
      }

      const payload = {
        tamanho_maximo_arquivo_mb:   tamanhoMaximoArquivo,
        lgpd_ocultar_dados:          lgpdOcultarDados,
        numero_linhas_grid:          numeroLinhasGrid,
        numero_max_arquivos_por_doc: maxArquivos,
        permite_arquivos_em_aditivo: permiteArquivosEmAditivo,
        agrupar_notificacoes:        agruparNotificacoes,
        alertas_vencimento_diario:   alertasVencimentoDiario,
        logotipo:                    logotipo || null,
      };
      if (idEntidade) {
        await api.put(
          `${BASE_URL}/api/empresasConfiguracoes/editarConfiguracoes/${idEntidade}`,
          payload,
          { headers: authHeaders }
        );
      } else {
        localStorage.setItem(`empresa_config_temp_${entidadeID}`, JSON.stringify(payload));
      }
      setGlobalData((prev) => ({ ...prev, numero_linhas_grid: numeroLinhasGrid, logotipo: logotipo || null }));

      if (resultadoPlano?.message) {
        mensagemFinal =
          resultadoPlano.tipo === "checkout"
            ? `Configurações salvas. ${resultadoPlano.message}`
            : resultadoPlano.message;
      } else if (resultadoPlano?.tipo === "alterado") {
        mensagemFinal = "Configurações salvas com sucesso!";
      }
      toast.success(mensagemFinal);
      handleClose();
    } catch (err) {
      const mensagem = err.response?.data?.message || "Erro ao salvar configurações. Tente novamente.";
      if (err.response?.data?.code === "USO_INCOMPATIVEL_PLANO") {
        toast.warning(mensagem);
      }
      setError(mensagem);
    } finally {
      setSalvando(false);
    }
  };

  // ── helpers ──────────────────────────────────────────────────────────────────
  const renderAlerta = (msg, tipo) => (
    <Alert variant={tipo} className="empresa-config-alerta">
      {tipo === "success" ? <FiCheckCircle aria-hidden="true" /> : <FiLock aria-hidden="true" />}
      <span>{msg}</span>
    </Alert>
  );

  const Switch = ({ id, checked, onChange, label, hint }) => (
    <div className="empresa-config-card empresa-config-switch">
      <div>
        <div className="empresa-config-label">{label}</div>
        {hint && <div className="empresa-config-hint">{hint}</div>}
      </div>
      <Form.Check type="switch" id={id} checked={checked} onChange={onChange} disabled={salvando} />
    </div>
  );

  // ── abas ─────────────────────────────────────────────────────────────────────
  const renderAba = () => {
    switch (abaAtiva) {

      case "geral":
        return (
          <>
            <input ref={fileInputRef} type="file" accept={ACCEPT_IMAGES}
              onChange={handleLogoChange} className="d-none" id="logotipo-upload" />
            <div className="empresa-config-card empresa-config-logo-card">
              <div className="empresa-config-card-title">
                <FiImage aria-hidden="true" />
                <span>Logotipo da empresa</span>
              </div>
              {logotipo ? (
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{
                    padding: 10, borderRadius: 8,
                    background: "var(--surface-card)",
                    border: "1px solid var(--border-default)",
                  }}>
                    <img src={logotipo} alt="Logotipo"
                      style={{ maxHeight: 80, maxWidth: 180, objectFit: "contain", display: "block" }} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <Button as="label" htmlFor="logotipo-upload" variant="outline-secondary"
                      size="sm" disabled={salvando} style={{ cursor: "pointer" }}>
                      <FiUpload size={13} className="me-1" />Trocar
                    </Button>
                    <Button variant="outline-danger" size="sm"
                      onClick={handleRemoveLogo} disabled={salvando}>
                      <FiTrash2 size={13} className="me-1" />Remover
                    </Button>
                  </div>
                </div>
              ) : (
                <Row className="align-items-center">
                  <Col xs="auto">
                    <Button as="label" htmlFor="logotipo-upload" variant="outline-primary"
                      size="sm" disabled={salvando} style={{ cursor: "pointer" }}>
                      <FiUpload size={13} className="me-1" />Selecionar imagem
                    </Button>
                  </Col>
                  <Col>
                    <div className="empresa-config-hint">JPEG, PNG, GIF ou WebP · máx. {tamanhoMaximoArquivo} MB</div>
                  </Col>
                </Row>
              )}
            </div>
          </>
        );

      case "armazenamento":
        return (
          <>
            <div className="empresa-config-card">
              <Form.Label className="empresa-config-label">Tamanho máximo de upload</Form.Label>
              <Form.Select size="sm" value={tamanhoMaximoArquivo}
                onChange={(e) => setTamanhoMaximoArquivo(Number(e.target.value))}
                disabled={salvando}>
                {TAMANHOS_PADRAO_MB.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
              <div className="empresa-config-hint">Limite por arquivo para todos os uploads desta entidade.</div>
            </div>

            <div className="empresa-config-card">
              <Form.Label className="empresa-config-label">Máximo de arquivos por documento</Form.Label>
              <Form.Control size="sm" type="number" min={0}
                value={numeroMaxArquivosPorDoc}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setNumeroMaxArquivosPorDoc(!Number.isNaN(v) && v >= 0 ? v : 0);
                }}
                disabled={salvando} />
              <div className="empresa-config-hint">0 = sem limite de anexos por documento.</div>
            </div>

            <Switch
              id="permite_arquivos_em_aditivo"
              checked={permiteArquivosEmAditivo === "S"}
              onChange={() => setPermiteArquivosEmAditivo((p) => p === "S" ? "N" : "S")}
              label="Permite arquivos em aditivo"
              hint="Habilita anexos em aditivos de documentos."
            />
          </>
        );

      case "interface":
        return (
          <div className="empresa-config-card">
            <Form.Label className="empresa-config-label">Linhas por página nas grids</Form.Label>
            <Form.Select size="sm" value={numeroLinhasGrid}
              onChange={(e) => setNumeroLinhasGrid(Number(e.target.value))}
              disabled={salvando}>
              {OPCOES_LINHAS_GRID.map((n) => (
                <option key={n} value={n}>{n} linhas</option>
              ))}
            </Form.Select>
            <div className="empresa-config-hint">Quantidade de registros exibidos por página nas grids do sistema.</div>
          </div>
        );

      case "plano":
        // Renderizado sempre (fora do switch, ver abaixo) para preservar a
        // seleção pendente ao trocar de aba antes de salvar.
        return null;

      case "privacidade":
        return (
          <Switch
            id="lgpd_ocultar_dados"
            checked={lgpdOcultarDados === "S"}
            onChange={() => setLgpdOcultarDados((p) => p === "S" ? "N" : "S")}
            label="LGPD — Ocultar dados sensíveis"
            hint="Mascara campos pessoais na exibição para usuários sem permissão."
          />
        );

      case "comunicacao":
        return (
          <>
            <Switch
              id="agrupar_notificacoes"
              checked={agruparNotificacoes === "S"}
              onChange={() => setAgruparNotificacoes((p) => p === "S" ? "N" : "S")}
              label="Agrupar Notificações"
              hint="Envia um único e-mail diário por usuário com todos os documentos pendentes, ignorando os horários individuais de cada agendamento."
            />
            <Switch
              id="alertas_vencimento_diario"
              checked={alertasVencimentoDiario === "S"}
              onChange={() => setAlertasVencimentoDiario((p) => p === "S" ? "N" : "S")}
              label="Envio Diário de Alertas de Vencimento"
              hint={
                alertasVencimentoDiario === "S"
                  ? "Ativado — o sistema enviará um e-mail por dia para cada usuário vinculado, todos os dias a partir da data de antecedência configurada no tipo de agendamento até que o documento seja renovado (aditivo) ou vença."
                  : "Desativado — para cada alerta configurado no documento, o sistema enviará um e-mail na data de antecedência definida (ex.: 30 dias antes, 14 dias antes). Além disso, um único e-mail é enviado no próprio dia do vencimento, independente da quantidade de alertas. Ideal para quem prefere avisos pontuais sem lembretes diários."
              }
            />
          </>
        );

      case "api":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="empresa-config-card">
              <div className="empresa-config-card-title">
                <FiKey aria-hidden="true" />
                <span>Chave de API</span>
              </div>
              <div className="empresa-config-hint" style={{ marginBottom: 12 }}>
                Use esta chave para autenticar requisições à API pública do GDoc.
                Envie o header <code>Authorization: Bearer &lt;chave&gt;</code> em cada chamada.
                A chave é vinculada a esta entidade e dá acesso apenas aos seus dados.
              </div>

              {apiKeyGerada && (
                <div style={{
                  background: "var(--surface-highlight, #f0fdf4)",
                  border: "1px solid var(--color-success, #16a34a)",
                  borderRadius: 8, padding: "12px 14px", marginBottom: 12,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <strong style={{ fontSize: 13, color: "var(--color-success, #16a34a)" }}>
                      ✓ Chave gerada — copie agora, ela não será exibida novamente.
                    </strong>
                    <Button variant="outline-success" size="sm" onClick={handleCopiarChave}>
                      {apiKeyCopiada ? <><FiCheckCircle size={13} className="me-1" />Copiada!</> : <><FiCopy size={13} className="me-1" />Copiar</>}
                    </Button>
                  </div>
                  <code style={{
                    display: "block", wordBreak: "break-all", fontSize: 12,
                    background: "var(--surface-card)", padding: "8px 10px", borderRadius: 6,
                    userSelect: "all",
                  }}>
                    {apiKeyGerada}
                  </code>
                </div>
              )}

              {apiKeyInfo ? (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <code style={{
                    flex: 1, background: "var(--surface-card)",
                    border: "1px solid var(--border-default)", borderRadius: 6,
                    padding: "7px 10px", fontSize: 13,
                    color: "var(--text-secondary)",
                  }}>
                    {apiKeyInfo.key_prefix}••••••••••••••••••••••••••••••••••••••••••••••••••••••••
                  </code>
                </div>
              ) : (
                !apiKeyGerada && (
                  <div className="empresa-config-hint" style={{ marginBottom: 12 }}>
                    Nenhuma chave ativa. Clique em "Gerar Chave" para criar uma.
                  </div>
                )
              )}

              {apiKeyInfo && (
                <div className="empresa-config-hint" style={{ marginBottom: 12 }}>
                  Criada em: {new Date(apiKeyInfo.created_at).toLocaleString("pt-BR")}
                  {apiKeyInfo.last_used_at && (
                    <> &nbsp;·&nbsp; Último uso: {new Date(apiKeyInfo.last_used_at).toLocaleString("pt-BR")}</>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  variant={apiKeyInfo ? "outline-warning" : "outline-primary"}
                  size="sm"
                  onClick={handleGerarApiKey}
                  disabled={apiKeyGerando || apiKeyRevogando}
                >
                  {apiKeyGerando
                    ? <><Spinner size="sm" className="me-1" />Gerando...</>
                    : <><FiRefreshCw size={13} className="me-1" />{apiKeyInfo ? "Gerar Nova Chave" : "Gerar Chave"}</>
                  }
                </Button>
                {apiKeyInfo && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleRevogarApiKey}
                    disabled={apiKeyGerando || apiKeyRevogando}
                  >
                    {apiKeyRevogando
                      ? <><Spinner size="sm" className="me-1" />Revogando...</>
                      : <><FiXCircle size={13} className="me-1" />Revogar Chave</>
                    }
                  </Button>
                )}
              </div>
            </div>

            <div className="empresa-config-card">
              <div className="empresa-config-card-title">
                <FiKey aria-hidden="true" />
                <span>Documentação da API</span>
              </div>
              <div className="empresa-config-hint">
                Acesse a documentação interativa (Swagger) para explorar e testar todos os endpoints disponíveis.
              </div>
              <Button
                as="a"
                href={`${BASE_URL}/api/docs`}
                target="_blank"
                rel="noopener noreferrer"
                variant="outline-secondary"
                size="sm"
                style={{ marginTop: 8 }}
              >
                Abrir documentação →
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <>
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      dialogClassName="empresa-config-dialog"
      contentClassName="empresa-config-modal"
    >
      <Modal.Header closeButton className="empresa-config-header">
        <div className="empresa-config-header-main">
          <div className="empresa-config-header-icon">
            <FiSettings size={22} aria-hidden="true" />
          </div>
          <div>
            <span className="empresa-config-eyebrow">Preferências da entidade</span>
            <Modal.Title>Configurações da Empresa</Modal.Title>
            <p>{empresaNome || "Nova empresa"}</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="empresa-config-body">
        <nav className="empresa-config-nav" aria-label="Seções das configurações">
          <div className="empresa-config-nav-title">Configurações</div>
          {ABAS_VISIVEIS.map((aba) => (
            <button
              key={aba.key}
              type="button"
              data-tour={`pref-tab-${aba.key}`}
              onClick={() => setAbaAtiva(aba.key)}
              className={`empresa-config-nav-item${abaAtiva === aba.key ? " active" : ""}`}
              aria-current={abaAtiva === aba.key ? "page" : undefined}
            >
              <aba.icon size={17} aria-hidden="true" />
              <span>{aba.label}</span>
            </button>
          ))}
        </nav>

        <section className="empresa-config-content">
          <div className="empresa-config-content-header">
            <div>
              <span className="empresa-config-eyebrow">Configuração</span>
              <h3>{ABAS_VISIVEIS.find((aba) => aba.key === abaAtiva)?.label}</h3>
            </div>
          </div>
          <div className="empresa-config-content-scroll">
          {loading ? (
            <div className="empresa-config-loading">
              <Spinner animation="border" />
              <strong>Carregando configurações</strong>
              <span>Preparando as preferências desta empresa.</span>
            </div>
          ) : (
            <>
              {error && renderAlerta(error, "danger")}
              {!empresaId && renderAlerta("Estas configurações serão aplicadas após salvar a empresa.", "info")}
              {renderAba()}
              {/* Sempre montado (não só quando abaAtiva === "plano") para não
                  perder a seleção pendente ao trocar de aba antes de salvar. */}
              <div style={{ display: abaAtiva === "plano" ? "block" : "none" }}>
                <SelecaoPlano ref={selecaoPlanoRef} idEntidade={idEntidade} empresaNome={empresaNome} />
              </div>
            </>
          )}
          </div>
        </section>
      </Modal.Body>

      <Modal.Footer className="modal-footer-padrao empresa-config-footer">
        <div className="modal-footer-acoes-esquerda">
          <FiShield size={15} aria-hidden="true" />
          <span>As alterações afetam todos os usuários desta entidade.</span>
        </div>
        <div className="modal-footer-acoes-direita">
          <Cancelar onClick={handleClose} disabled={salvando} />
          <Salvar onClick={handleSave} loading={salvando} disabled={loading} />
        </div>
      </Modal.Footer>
    </Modal>
  </>
  );
};

export default EmpresasConfiguracoesModal;
